import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  Group,
  Text,
  Select,
  Button,
  Alert,
  Stack,
  Table,
  Badge,
  ScrollArea,
  SegmentedControl,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconCheck,
  IconX,
  IconWand,
  IconRefresh,
} from '@tabler/icons-react';
import { FieldMapping, ParsedFileData } from '../../../types/geneva-schema';
import { suggestFieldMappings } from '../../../utils/file-parser';
import { GenevaFieldRenderer, FieldWithOverride } from '../../../components/GenevaFieldRenderer';
import { BulkDataPreview } from '../../../components/BulkDataPreview';
import { MappingField } from '../../../utils/geneva-field-helpers';

interface BulkFieldMappingProps {
  parsedData: ParsedFileData;
  availableFields: MappingField[];
  onMappingComplete: (mappings: Record<string, FieldMapping>) => void;
}

export const BulkFieldMapping: React.FC<BulkFieldMappingProps> = ({
  parsedData,
  availableFields,
  onMappingComplete,
}) => {
  const [fieldMappings, setFieldMappings] = useState<Record<string, FieldMapping>>({});
  const [defaultValues, setDefaultValues] = useState<Record<string, any>>({});
  const [fieldModes, setFieldModes] = useState<Record<string, 'column' | 'default'>>({});
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const enhancedAvailableFields = useMemo(() => {
    const baseFieldsByKey = new Map<string, MappingField>(
      availableFields.map(field => [field.xmlTag, field]),
    );

    const dynamicFields = new Map<string, MappingField>();

    parsedData.headers.forEach(header => {
      const trimmedHeader = header.trim();
      const match = trimmedHeader.match(/^(.+)\[(\d+)\]\.(.+)$/);
      if (!match) {
        return;
      }

      const basePath = match[1];
      const instanceIndex = parseInt(match[2], 10);
      const fieldName = match[3];
      if (Number.isNaN(instanceIndex)) {
        return;
      }

      const baseFieldKey = `${basePath}.${fieldName}`;
      const baseField = baseFieldsByKey.get(baseFieldKey);
      if (!baseField) {
        return;
      }

      if (dynamicFields.has(trimmedHeader)) {
        return;
      }

      dynamicFields.set(trimmedHeader, {
        ...baseField,
        xmlTag: trimmedHeader,
        required: false,
        description: baseField.description
          ? `${baseField.description} (Instance ${instanceIndex})`
          : baseField.description,
      });
    });

    if (dynamicFields.size === 0) {
      return availableFields;
    }

    return [...availableFields, ...dynamicFields.values()];
  }, [availableFields, parsedData.headers]);

  const requiredFields = useMemo(() => 
    enhancedAvailableFields.filter(f => f.required).map(f => f.xmlTag),
    [enhancedAvailableFields]
  );

  const columnOptions = useMemo(() => [
    { value: '', label: '-- Select Column --' },
    ...parsedData.headers.map((header, index) => ({
      value: index.toString(),
      label: `${header} (Column ${index + 1})`,
    })),
  ], [parsedData.headers]);

  // Auto-suggest mappings on initial load
  useEffect(() => {
    const suggestions = suggestFieldMappings(parsedData.headers, enhancedAvailableFields);
    setFieldMappings(suggestions);
    
    // Initialize field modes - default to 'column' for suggested mappings, 'default' for others
    const initialModes: Record<string, 'column' | 'default'> = {};
    enhancedAvailableFields.forEach(field => {
      initialModes[field.xmlTag] = suggestions[field.xmlTag] ? 'column' : 'default';
    });
    setFieldModes(initialModes);
  }, [parsedData.headers, enhancedAvailableFields]);

  // Validate mappings and default values whenever they change
  useEffect(() => {
    const errors: string[] = [];
    
    // Check that all required fields have either a mapping or a default value
    requiredFields.forEach(fieldName => {
      const hasMapping = fieldMappings[fieldName] && fieldModes[fieldName] === 'column';
      const defaultValue = defaultValues[fieldName];
      const hasDefaultValue = fieldModes[fieldName] === 'default' && 
        defaultValue !== undefined && 
        defaultValue !== '' && 
        defaultValue !== null;
      
      if (!hasMapping && !hasDefaultValue) {
        errors.push(`Required field '${fieldName}' must have either a column mapping or a default value`);
      }
    });
    
    // Check for duplicate column mappings (but skip default values which have columnIndex = -1)
    const usedColumns = new Map<number, string>();
    Object.entries(fieldMappings).forEach(([fieldName, mapping]) => {
      if (fieldModes[fieldName] === 'column') { // Only check column mappings
        if (usedColumns.has(mapping.columnIndex)) {
          errors.push(
            `Column "${mapping.columnName}" is mapped to multiple fields: "${fieldName}" and "${usedColumns.get(mapping.columnIndex)}"`
          );
        } else {
          usedColumns.set(mapping.columnIndex, fieldName);
        }
      }
    });
    
    setValidationErrors(errors);
    
    // Create combined mapping data that includes both column mappings and default values
    const combinedMappings: Record<string, FieldMapping> = {};
    
    // Add column mappings
    Object.entries(fieldMappings).forEach(([fieldName, mapping]) => {
      if (fieldModes[fieldName] === 'column') {
        combinedMappings[fieldName] = mapping;
      }
    });
    
    // Add default value mappings
    Object.entries(defaultValues).forEach(([fieldName, value]) => {
      if (fieldModes[fieldName] === 'default' && 
          value !== undefined && 
          value !== '' && 
          value !== null) {
        const field = enhancedAvailableFields.find(f => f.xmlTag === fieldName);
        if (field) {
          combinedMappings[fieldName] = {
            fieldName: field.xmlTag,
            columnIndex: -1, // Special value to indicate default value
            columnName: '', // Not applicable for default values
            isRequired: field.required,
            dataType: field.type,
            defaultValue: value,
          };
        }
      }
    });
    
    if (errors.length === 0 && Object.keys(combinedMappings).length > 0) {
      onMappingComplete(combinedMappings);
    }
  }, [fieldMappings, defaultValues, fieldModes, requiredFields, enhancedAvailableFields, onMappingComplete]);

  const handleFieldMappingChange = (fieldName: string, columnIndex: string) => {
    if (columnIndex === '') {
      // Remove mapping
      const updatedMappings = { ...fieldMappings };
      delete updatedMappings[fieldName];
      setFieldMappings(updatedMappings);
      return;
    }

    const colIndex = parseInt(columnIndex, 10);
    const field = enhancedAvailableFields.find(f => f.xmlTag === fieldName);
    
    if (!field) return;

    setFieldMappings(prev => ({
      ...prev,
      [fieldName]: {
        fieldName: field.xmlTag,
        columnIndex: colIndex,
        columnName: parsedData.headers[colIndex],
        isRequired: field.required,
        dataType: field.type,
      },
    }));
  };

  const handleAutoMapping = () => {
    const suggestions = suggestFieldMappings(parsedData.headers, enhancedAvailableFields);
    setFieldMappings(suggestions);
    
    // Update field modes for auto-suggested mappings
    const updatedModes = { ...fieldModes };
    enhancedAvailableFields.forEach(field => {
      updatedModes[field.xmlTag] = suggestions[field.xmlTag] ? 'column' : 'default';
    });
    setFieldModes(updatedModes);
  };

  const handleClearMappings = () => {
    setFieldMappings({});
    setDefaultValues({});
    // Reset all modes to default
    const clearedModes: Record<string, 'column' | 'default'> = {};
    enhancedAvailableFields.forEach(field => {
      clearedModes[field.xmlTag] = 'default';
    });
    setFieldModes(clearedModes);
  };

  const handleFieldModeChange = (fieldName: string, mode: 'column' | 'default') => {
    setFieldModes(prev => ({ ...prev, [fieldName]: mode }));
    
    if (mode === 'column') {
      // Clear default value when switching to column mode
      const updatedDefaults = { ...defaultValues };
      delete updatedDefaults[fieldName];
      setDefaultValues(updatedDefaults);
    } else {
      // Clear field mapping when switching to default mode
      const updatedMappings = { ...fieldMappings };
      delete updatedMappings[fieldName];
      setFieldMappings(updatedMappings);
    }
  };

  const handleDefaultValueChange = (fieldName: string, value: any) => {
    setDefaultValues(prev => ({ ...prev, [fieldName]: value }));
  };

  const getFieldStatus = (fieldName: string) => {
    const field = enhancedAvailableFields.find(f => f.xmlTag === fieldName);
    const isRequired = field?.required || false;
    const mode = fieldModes[fieldName] || 'default';
    
    const hasMapping = mode === 'column' && fieldMappings[fieldName];
    const defaultValue = defaultValues[fieldName];
    const hasDefaultValue = mode === 'default' && 
      defaultValue !== undefined && 
      defaultValue !== '' && 
      defaultValue !== null;
    
    const hasValue = hasMapping || hasDefaultValue;

    if (isRequired && !hasValue) {
      return { color: 'red', label: 'Required - Missing', icon: IconX };
    } else if (!isRequired && !hasValue) {
      return { color: 'gray', label: 'Optional - Not Set', icon: null };
    } else if (hasMapping) {
      return { color: 'green', label: 'Mapped to Column', icon: IconCheck };
    } else if (hasDefaultValue) {
      return { color: 'blue', label: 'Default Value Set', icon: IconCheck };
    } else {
      return { color: 'gray', label: 'Not Set', icon: null };
    }
  };

  const previewData = useMemo(() => {
    const hasColumnMappings = Object.values(fieldModes).some(mode => mode === 'column');
    const hasDefaultValues = Object.values(fieldModes).some(mode => mode === 'default');
    
    if (!hasColumnMappings && !hasDefaultValues) return [];
    
    // Transform all rows
    return parsedData.rows.map((row) => {
      const transformedRow: Record<string, any> = {};
      
      // Add column mappings
      Object.entries(fieldMappings).forEach(([fieldName, mapping]) => {
        if (fieldModes[fieldName] === 'column') {
          transformedRow[fieldName] = row[mapping.columnIndex];
        }
      });
      
      // Add default values
      Object.entries(defaultValues).forEach(([fieldName, value]) => {
        if (fieldModes[fieldName] === 'default') {
          transformedRow[fieldName] = value;
        }
      });
      
      return transformedRow;
    });
  }, [fieldMappings, defaultValues, fieldModes, parsedData.rows]);

  // Create combined field mappings for the preview component
  const combinedFieldMappings = useMemo(() => {
    const combined: Record<string, any> = {};
    
    // Add column mappings
    Object.entries(fieldMappings).forEach(([fieldName, mapping]) => {
      if (fieldModes[fieldName] === 'column') {
        combined[fieldName] = mapping;
      }
    });
    
    // Add default value mappings  
    Object.entries(defaultValues).forEach(([fieldName, value]) => {
      if (fieldModes[fieldName] === 'default') {
        combined[fieldName] = { defaultValue: value };
      }
    });
    
    return combined;
  }, [fieldMappings, defaultValues, fieldModes]);

  const configuredFieldsCount = enhancedAvailableFields.filter(field => {
    const mode = fieldModes[field.xmlTag];
    const hasMapping = mode === 'column' && fieldMappings[field.xmlTag];
    const defaultValue = defaultValues[field.xmlTag];
    const hasDefaultValue = mode === 'default' && 
      defaultValue !== undefined && 
      defaultValue !== '' && 
      defaultValue !== null;
    return hasMapping || hasDefaultValue;
  }).length;
  
  const requiredConfiguredCount = requiredFields.filter(fieldName => {
    const mode = fieldModes[fieldName];
    const hasMapping = mode === 'column' && fieldMappings[fieldName];
    const defaultValue = defaultValues[fieldName];
    const hasDefaultValue = mode === 'default' && 
      defaultValue !== undefined && 
      defaultValue !== '' && 
      defaultValue !== null;
    return hasMapping || hasDefaultValue;
  }).length;

  return (
    <Stack gap="md">
      {/* Header and Actions */}
      <Card withBorder>
        <Group justify="space-between" mb="sm">
          <Box>
            <Text fw={500}>Map File Columns to Geneva Fields</Text>
            <Text size="sm" c="dimmed">
              {parsedData.fileName} • {parsedData.headers.length} columns • {parsedData.rowCount} data rows
            </Text>
          </Box>
          
          <Group gap="sm">
            <Button
              variant="light"
              leftSection={<IconWand size={16} />}
              onClick={handleAutoMapping}
            >
              Auto Map
            </Button>
            <Button
              variant="light"
              color="red"
              leftSection={<IconRefresh size={16} />}
              onClick={handleClearMappings}
            >
              Clear All
            </Button>
          </Group>
        </Group>

        {/* Configuration Status */}
        <Group gap="lg">
          <Badge color="blue" variant="light">
            {configuredFieldsCount} fields configured
          </Badge>
          <Badge color={requiredConfiguredCount === requiredFields.length ? 'green' : 'red'} variant="light">
            {requiredConfiguredCount}/{requiredFields.length} required fields
          </Badge>
        </Group>
      </Card>

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Mapping Issues"
          color="red"
        >
          <Stack gap="xs">
            {validationErrors.map((error, index) => (
              <Text key={index} size="sm">• {error}</Text>
            ))}
          </Stack>
        </Alert>
      )}

      {/* Field Configuration Table */}
      <Card withBorder>
        <Text fw={500} mb="sm">Field Configuration</Text>
        <ScrollArea>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Geneva Field</Table.Th>
                <Table.Th>Type</Table.Th>
                <Table.Th>Required</Table.Th>
                <Table.Th>Input Mode</Table.Th>
                <Table.Th>Configuration</Table.Th>
                <Table.Th>Status</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {enhancedAvailableFields.map((field) => {
                const status = getFieldStatus(field.xmlTag);
                const mapping = fieldMappings[field.xmlTag];
                const mode = fieldModes[field.xmlTag] || 'default';
                const defaultValue = defaultValues[field.xmlTag];
                
                const fieldWithOverride: FieldWithOverride = {
                  ...field,
                  isOverridden: false,
                };
                
                return (
                  <Table.Tr key={field.xmlTag}>
                    <Table.Td>
                      <Box>
                        <Text size="sm" fw={500}>{field.xmlTag}</Text>
                        {field.description && (
                          <Text size="xs" c="dimmed">{field.description}</Text>
                        )}
                      </Box>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="sm" variant="outline">
                        {field.type}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      {field.required ? (
                        <Badge size="sm" color="red" variant="light">Required</Badge>
                      ) : (
                        <Badge size="sm" color="gray" variant="light">Optional</Badge>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <SegmentedControl
                        value={mode}
                        onChange={(value) => handleFieldModeChange(field.xmlTag, value as 'column' | 'default')}
                        data={[
                          { label: 'Column', value: 'column' },
                          { label: 'Default', value: 'default' },
                        ]}
                        size="xs"
                      />
                    </Table.Td>
                    <Table.Td>
                      {mode === 'column' ? (
                        <Select
                          placeholder="Select column"
                          data={columnOptions}
                          value={mapping ? mapping.columnIndex.toString() : ''}
                          onChange={(value) => handleFieldMappingChange(field.xmlTag, value || '')}
                          size="sm"
                          style={{ minWidth: 200 }}
                        />
                      ) : (
                        <Box style={{ minWidth: 200 }}>
                          <GenevaFieldRenderer
                            field={fieldWithOverride}
                            value={defaultValue}
                            onChange={handleDefaultValueChange}
                            showLabel={false}
                            showDescription={false}
                            showOverrideBadges={false}
                            placeholder={`Default ${field.xmlTag}`}
                          />
                        </Box>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        {status.icon && <status.icon size={16} color={`var(--mantine-color-${status.color}-6)`} />}
                        <Badge size="sm" color={status.color} variant="light">
                          {status.label}
                        </Badge>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </Card>

      {/* Data Preview */}
      {previewData.length > 0 && (
        <BulkDataPreview
          data={previewData}
          fieldMappings={combinedFieldMappings}
          fieldModes={fieldModes}
          title="Mapped Data Preview"
          maxHeight={500}
          showRowLimit={true}
        />
      )}
    </Stack>
  );
};